import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronUp, ChevronDown, Play, Pause, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { api, lyricStanzas, fileUrl } from "../lib/api";
import { parseTimings, lineAt, useFitSize, canSetVolume } from "../lib/karaoke";
import { Bini, BiniBubble } from "../components/Bini";
import { t } from "../i18n";

const CtrlBtn = ({ onClick, label, testId, color = "#fff", ink = true, children }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testId}
    className={`btn-3d no-shine !min-h-[60px] !min-w-[60px] !px-3 ${ink ? "is-ink" : ""}`} style={{ "--c": color }}>
    {children}
  </button>
);

export default function Karaoke() {
  const { slug } = useParams();
  const [song, setSong] = useState(null);
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(false);
  const [secs, setSecs] = useState(4);
  const [missing, setMissing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [volumeOk] = useState(canSetVolume);
  const lineRefs = useRef([]);
  const audioRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { api.get(`/songs/${slug}`).then((r) => setSong(r.data)).catch(() => setMissing(true)); }, [slug]);

  const lines = useMemo(() => {
    const out = [];
    lyricStanzas(song?.lyrics).forEach((st, si) => st.forEach((text, li) => out.push({ text, stanzaStart: si > 0 && li === 0 })));
    return out;
  }, [song]);

  const hasAudio = !!song?.audio_path;
  const times = useMemo(() => {
    const parsed = parseTimings(song?.timings, lines.length);
    if (parsed || !duration) return parsed;
    return lines.map((_, i) => (duration * i) / lines.length);
  }, [song, lines, duration]);
  const fontSize = useFitSize(lines, boxRef);

  // Timer mode only when the song has no music
  useEffect(() => {
    if (hasAudio || !auto || !lines.length) return;
    const id = setInterval(() => setIdx((i) => (i + 1 < lines.length ? i + 1 : i)), secs * 1000);
    return () => clearInterval(id);
  }, [hasAudio, auto, secs, lines.length]);

  useEffect(() => { if (!hasAudio && auto && idx === lines.length - 1) setAuto(false); }, [hasAudio, idx, auto, lines.length]);

  // Music mode: the highlighted line follows the real playback position
  useEffect(() => {
    if (!hasAudio || !auto || !times) return;
    let raf;
    const tick = () => {
      const a = audioRef.current;
      if (a) setIdx(lineAt(times, a.currentTime));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [hasAudio, auto, times]);

  useEffect(() => { if (audioRef.current) { audioRef.current.muted = muted; audioRef.current.volume = volume; } }, [muted, volume, hasAudio]);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    lineRefs.current[idx]?.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
  }, [idx]);

  const seekLine = (i) => {
    const n = Math.min(Math.max(i, 0), lines.length - 1);
    setIdx(n);
    if (hasAudio && audioRef.current && times) audioRef.current.currentTime = times[n] + 0.05;
  };
  const go = (d) => seekLine(idx + d);
  const toggle = () => {
    const a = audioRef.current;
    if (!hasAudio) return setAuto((v) => !v);
    if (a.paused) a.play().catch(() => setAuto(false));
    else a.pause();
  };
  const restart = () => {
    setIdx(0);
    if (hasAudio && audioRef.current) audioRef.current.currentTime = 0;
    else setAuto(false);
  };

  return (
    <div className="min-h-[100dvh] bg-[#1E2A5A] text-white flex flex-col">
      <header className="sticky top-0 z-10 flex items-center gap-3 bg-[#1E2A5A]/95 backdrop-blur px-4 py-3">
        <Link to={`/comptines/${slug}`} data-testid="karaoke-back-btn" aria-label={t("nav.back")} className="btn-3d is-off !min-h-[56px] !w-14 !p-0">
          <ArrowLeft className="w-7 h-7" strokeWidth={3} />
        </Link>
        <span className={`inline-block ${auto ? "bini-dance" : ""}`}><span key={idx} className="inline-block bini-hop"><Bini size={48} /></span></span>
        <h1 data-testid="karaoke-title" className="text-xl sm:text-2xl font-extrabold truncate">{song?.title || t("karaoke.title")}</h1>
      </header>

      <div aria-hidden="true" data-testid="karaoke-bini" data-dancing={auto}
        className="pointer-events-none fixed left-8 top-1/2 z-10 hidden -translate-y-1/2 lg:block xl:left-20">
        <div className={auto ? "bini-dance" : ""}><div key={idx} className="bini-hop"><Bini size={150} /></div></div>
      </div>

      <main className="flex-1 px-3 sm:px-10 py-[30vh]" data-testid="karaoke-lines">
        {hasAudio && (
          <audio ref={audioRef} src={fileUrl(`/files/songs/${song.id}/audio`)} preload="auto" playsInline data-testid="karaoke-audio"
            onPlay={() => setAuto(true)} onPause={() => setAuto(false)} onEnded={() => setAuto(false)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)} />
        )}
        {missing && <BiniBubble testId="karaoke-not-found" className="max-w-lg mx-auto">{t("soon.notFound")}</BiniBubble>}
        {song && !lines.length && <BiniBubble testId="karaoke-empty" className="max-w-lg mx-auto">{t("songs.noLyrics")}</BiniBubble>}
        <div ref={boxRef} className="mx-auto max-w-4xl text-center">
          {lines.map((l, i) => (
            <p key={i} ref={(el) => (lineRefs.current[i] = el)} onClick={() => seekLine(i)} data-testid={`karaoke-line-${i}`}
              data-active={i === idx} style={{ fontSize }}
              className={`font-display font-extrabold leading-tight whitespace-nowrap cursor-pointer transition-[opacity,transform,color] duration-300 py-2 ${l.stanzaStart ? "mt-10" : ""} ${i === idx ? "text-[#FFD21F] opacity-100 scale-105" : "opacity-35"}`}>
              {l.text}
            </p>
          ))}
        </div>
      </main>

      {lines.length > 0 && (
        <footer className="sticky bottom-0 bg-[#1E2A5A]/95 backdrop-blur px-4 pt-4 pb-6 border-t-4 border-white/10">
          <div className="mx-auto max-w-3xl flex flex-nowrap items-center justify-center gap-2 sm:gap-3">
            <CtrlBtn onClick={() => go(-1)} label={t("karaoke.prev")} testId="karaoke-prev-btn"><ChevronUp className="w-8 h-8" strokeWidth={3} /></CtrlBtn>
            <CtrlBtn onClick={toggle} label={auto ? t("karaoke.pause") : t("karaoke.play")} testId="karaoke-auto-toggle-btn" color={auto ? "#FF8A1F" : "#FFD21F"} ink={!auto}>
              {auto ? <Pause className="w-8 h-8 fill-current" /> : <Play className="w-8 h-8 fill-current" />}
              <span className="text-base">{auto ? "Pause" : hasAudio ? t("karaoke.play") : "Auto"}</span>
            </CtrlBtn>
            <CtrlBtn onClick={() => go(1)} label={t("karaoke.next")} testId="karaoke-next-btn"><ChevronDown className="w-8 h-8" strokeWidth={3} /></CtrlBtn>
            <CtrlBtn onClick={restart} label={t("karaoke.restart")} testId="karaoke-restart-btn" color="#8A3FE0" ink={false}>
              <RotateCcw className="w-7 h-7" strokeWidth={3} />
            </CtrlBtn>
          </div>
          {hasAudio ? (
            <div className="mx-auto mt-4 max-w-md flex items-center justify-center gap-4 text-sm font-bold" data-testid="karaoke-sound">
              <button type="button" onClick={() => setMuted((m) => !m)} data-testid="karaoke-mute-btn" aria-pressed={muted}
                aria-label={muted ? t("karaoke.unmute") : t("karaoke.mute")}
                className={`btn-3d no-shine !min-h-[52px] !min-w-[52px] !p-0 ${muted ? "is-off" : ""}`} style={{ "--c": "#38B6FF" }}>
                {muted ? <VolumeX className="w-6 h-6" strokeWidth={2.5} /> : <Volume2 className="w-6 h-6" strokeWidth={2.5} />}
              </button>
              {volumeOk && (
                <Slider data-testid="karaoke-volume-slider" min={0} max={1} step={0.05} value={[volume]} disabled={muted}
                  onValueChange={([v]) => setVolume(v)} aria-label={t("karaoke.volume")} className="flex-1" />
              )}
            </div>
          ) : (
            <>
              <p className="mt-3 text-center text-sm font-semibold text-white/70" data-testid="karaoke-no-music">{t("karaoke.noMusic")}</p>
              <div className="mx-auto mt-3 max-w-md flex items-center gap-4 text-sm font-bold" data-testid="karaoke-speed">
                <span>{t("karaoke.slow")}</span>
                <Slider data-testid="karaoke-speed-slider" min={2} max={8} step={0.5} value={[10 - secs]}
                  onValueChange={([v]) => setSecs(10 - v)} aria-label={t("karaoke.speed")} className="flex-1" />
                <span>{t("karaoke.fast")}</span>
              </div>
            </>
          )}
        </footer>
      )}
    </div>
  );
}
